<template>
  <div class="flex flex-col font-ds">
    <!--
      The confirmation, centered rather than left-aligned like every other
      screen in this dialog — this is the one moment the wizard is not asking
      for anything, so it reads as an announcement rather than a form.
    -->
    <div class="flex flex-col items-center gap-4 px-6 pt-8 pb-6 text-center">
      <span class="inline-flex items-center justify-center size-14 shrink-0 rounded-full bg-success-subtle">
        <Check class="size-7 text-success" />
      </span>
      <div>
        <h3 class="text-2xl font-bold tracking-ds-snug text-foreground">
          {{ heading }}
        </h3>
        <p class="mt-1.5 max-w-115 text-sm font-medium leading-normal text-muted-foreground text-balance">
          {{ summary }}
        </p>
      </div>
    </div>

    <div class="flex flex-col gap-2.5 px-6">
      <!--
        One row per record the create actually wrote — every one of them, not
        only the first. A financed property's mortgage used to be creatable
        with no way back to it short of the accounts list; this is its own
        way in, same as the property's.
      -->
      <div
        v-for="row in records"
        :key="row.asset.id"
        class="flex items-center gap-3.5 px-4 py-3.5 border border-border rounded-ds-md shadow-ds-xs"
      >
        <span class="inline-flex items-center justify-center size-8.5 shrink-0 rounded-ds-sm bg-secondary text-muted-foreground">
          <component
            :is="row.icon"
            class="size-4"
          />
        </span>
        <div class="flex-1 min-w-0">
          <p class="truncate text-sm font-bold text-foreground">
            {{ row.asset.data.name }}
          </p>
          <p class="mt-0.5 text-xs font-medium text-muted-foreground">
            {{ row.label }} · {{ row.detail }}
          </p>
        </div>
        <p
          v-if="row.amount !== undefined"
          class="shrink-0 text-base font-bold tracking-ds-snug text-foreground"
        >
          {{ money(row.amount) }}
        </p>
        <DSButton
          variant="link"
          size="sm"
          class="h-auto shrink-0 px-0 font-bold"
          data-refid="View"
          @click="emit('open', row.asset)"
        >
          View
          <ArrowRight class="size-3.5" />
        </DSButton>
      </div>

      <!--
        Only where there is something to net — a single record has nothing
        else on this screen for its own amount to disagree with.
      -->
      <div
        v-if="netEquity !== undefined"
        class="flex items-center justify-between px-4 pt-1"
      >
        <span class="text-xs font-medium text-muted-foreground">Net equity added</span>
        <span
          class="text-base font-bold tracking-ds-snug"
          :class="netEquity < 0 ? 'text-destructive' : 'text-success'"
        >
          {{ money(netEquity) }}
        </span>
      </div>
    </div>

    <!--
      Named only when the ownership step actually made one — an account the
      repository already had is already named above, in the summary line.
    -->
    <div
      v-if="newAccountName"
      class="mx-6 mt-5 flex items-center gap-3 rounded-ds-md bg-accent px-4 py-3"
    >
      <Folder class="size-4 shrink-0 text-brand" />
      <p class="min-w-0 flex-1 text-sm font-medium text-accent-foreground">
        <strong class="font-bold">{{ newAccountName }}</strong> was created as a new account.
      </p>
      <DSButton
        v-if="newAccountAsset"
        variant="link"
        size="sm"
        class="h-auto shrink-0 px-0 font-bold"
        @click="emit('open', newAccountAsset)"
      >
        Open account
        <ArrowRight class="size-3.5" />
      </DSButton>
    </div>

    <div
      v-if="tags.length"
      class="mt-5 px-6"
    >
      <TagChips
        label="Tagged"
        :selected="tags"
        readonly
      />
    </div>

    <div class="mt-6 flex justify-end gap-2.5 border-t border-border bg-background px-6 py-4">
      <DSButton
        variant="secondary"
        @click="emit('add-another')"
      >
        Add another asset
      </DSButton>
      <DSButton @click="emit('go-to-balance-sheet')">
        Go to balance sheet
      </DSButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { ArrowRight, Check, Folder } from '@lucide/vue'

import { DSButton } from '@ds'
import { getCurrencySymbol } from '@/helpers/common'
import { numberFormat } from '@/helpers/numbers'
import { FRACTION_DIGITS } from '@/const'

import { AssetClass } from '@/entities/assets'
import { TagRef } from '@/entities/tags/utils/types'

import TagChips from '@/entities/tags/components/TagChips.vue'

import { CreatedRecordRow } from '../utils/helpers'

/*
 * The wizard's last screen, after create has actually written something —
 * see WizardDialog's handleCreate for how `records`, `netEquity` and
 * `newAccountName` are shaped from the same create() call.
 *
 * Presentational: it holds no create logic of its own and decides nothing
 * about the account or the amounts — that reading belongs to the helpers in
 * utils/helpers.ts, which is what stays unit-testable without a live Pinia.
 */
const { records, currency, tags = [] } = defineProps<{
  /** One row per record the create wrote, in the order it wrote them. */
  records: CreatedRecordRow[]
  /** What these records did to the balance sheet together — see netEquityOf. */
  netEquity?: number
  /** One sentence: what was made, and where — see createdSummaryText. */
  summary: string
  /** Named only when the ownership step created an account — see newlyCreatedAccountName. */
  newAccountName?: string
  /** That account's own entity, when it could be resolved. Absent leaves out the "Open account" link rather than a broken one. */
  newAccountAsset?: AssetClass
  /** The primary record's own tags — a mortgage carries none of its own. */
  tags?: TagRef[]
  /** What every amount on this screen is denominated in — the wizard's own currency field. */
  currency?: string
}>()

const emit = defineEmits<{
  /** Opens this asset's own drawer and ends the wizard. */
  (e: 'open', asset: AssetClass): void
  /** Back to the Add asset dialog, sentence intact, to describe another. */
  (e: 'add-another'): void
  /** Ends the wizard and takes the user to the balance sheet. */
  (e: 'go-to-balance-sheet'): void
}>()

const heading = computed(() => `${records.length} record${records.length === 1 ? '' : 's'} added`)

/*
 * Minimum zero fraction digits, not FRACTION_DIGITS: this echoes an amount
 * that was typed as a whole number, and "$750,000.00" claims a precision
 * nobody entered — same reasoning as WizardField's own number display.
 */
const money = (amount: number) =>
  numberFormat(amount, {
    currency: getCurrencySymbol(currency),
    fractionDigits: 0,
    fractionDigitsMax: FRACTION_DIGITS,
  })
</script>
